import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { ethers } from "ethers";
import {
  REQUIRED_CHAIN_ID,
  REQUIRED_CHAIN_ID_HEX,
  ADD_CHAIN_PARAMS,
} from "../config/network";

const WalletContext = createContext(null);
export const useWallet = () => useContext(WalletContext);

export function WalletProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [switching, setSwitching] = useState(false);
  // Bump this to tell data hooks "re-read the chain" — NEVER reload the page.
  const [refreshKey, setRefreshKey] = useState(0);

  // Provider/signer live in refs so re-renders don't recreate them (avoids loops).
  const providerRef = useRef(null);
  const signerRef = useRef(null);
  // Guard so we attempt an auto-switch at most once per wrong-chain event.
  const switchAttemptedFor = useRef(null);

  const eth = typeof window !== "undefined" ? window.ethereum : null;
  const isCorrectNetwork = chainId === REQUIRED_CHAIN_ID;

  const buildProvider = useCallback(() => {
    if (!eth) return null;
    const p = new ethers.providers.Web3Provider(eth, "any");
    providerRef.current = p;
    signerRef.current = p.getSigner();
    return p;
  }, [eth]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // ---- Network switching (no reload) -------------------------------------
  const switchNetwork = useCallback(async () => {
    if (!eth) return false;
    setSwitching(true);
    try {
      await eth.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: REQUIRED_CHAIN_ID_HEX }],
      });
      return true;
    } catch (err) {
      // 4902 / -32603: chain not added to wallet -> add it, then it auto-selects.
      if (err && (err.code === 4902 || err.code === -32603)) {
        try {
          await eth.request({
            method: "wallet_addEthereumChain",
            params: [ADD_CHAIN_PARAMS],
          });
          return true;
        } catch {
          return false;
        }
      }
      return false;
    } finally {
      setSwitching(false);
    }
  }, [eth]);

  // ---- Connect -----------------------------------------------------------
  const connect = useCallback(async () => {
    if (!eth) {
      throw new Error(
        "No wallet detected. Open this app inside your wallet's DApp browser (e.g. Trust Wallet)."
      );
    }
    setConnecting(true);
    try {
      const accounts = await eth.request({ method: "eth_requestAccounts" });
      buildProvider();
      const cidHex = await eth.request({ method: "eth_chainId" });
      const cid = parseInt(cidHex, 16);
      setChainId(cid);
      setAccount(accounts[0] ? ethers.utils.getAddress(accounts[0]) : null);
      // If on the wrong chain, immediately try to move them to the right one.
      if (cid !== REQUIRED_CHAIN_ID) {
        switchAttemptedFor.current = cid;
        await switchNetwork();
      }
      return accounts[0];
    } finally {
      setConnecting(false);
    }
  }, [eth, buildProvider, switchNetwork]);

  const disconnect = useCallback(() => {
    setAccount(null);
    signerRef.current = null;
  }, []);

  // ---- Wire wallet events ONCE (event-driven, no polling, no reload) ------
  useEffect(() => {
    if (!eth) return;

    const handleAccounts = (accounts) => {
      if (!accounts || accounts.length === 0) {
        disconnect();
      } else {
        setAccount(ethers.utils.getAddress(accounts[0]));
        buildProvider();
        refresh();
      }
    };

    const handleChain = (cidHex) => {
      const cid = parseInt(cidHex, 16);
      setChainId(cid);
      buildProvider(); // rebuild provider against the new chain
      // Reset the one-shot guard for the new chain so we can offer a switch.
      if (cid === REQUIRED_CHAIN_ID) {
        switchAttemptedFor.current = null;
      }
      refresh(); // tell hooks to re-read — WITHOUT reloading the page
    };

    eth.on("accountsChanged", handleAccounts);
    eth.on("chainChanged", handleChain);

    // Eager-connect if wallet already authorized this site.
    eth
      .request({ method: "eth_accounts" })
      .then((accounts) => {
        if (accounts && accounts.length > 0) {
          buildProvider();
          setAccount(ethers.utils.getAddress(accounts[0]));
          return eth.request({ method: "eth_chainId" });
        }
        return null;
      })
      .then((cidHex) => {
        if (cidHex) setChainId(parseInt(cidHex, 16));
      })
      .catch(() => {});

    return () => {
      if (eth.removeListener) {
        eth.removeListener("accountsChanged", handleAccounts);
        eth.removeListener("chainChanged", handleChain);
      }
    };
  }, [eth]);

  // ---- Auto-switch when connected but on the wrong chain (one-shot) ------
  useEffect(() => {
    if (!account || chainId == null) return;
    if (chainId === REQUIRED_CHAIN_ID) return;
    if (switchAttemptedFor.current === chainId) return; // already tried this chain
    switchAttemptedFor.current = chainId;
    switchNetwork();
  }, [account, chainId]);

  const value = {
    account,
    chainId,
    isCorrectNetwork,
    connecting,
    switching,
    connect,
    disconnect,
    switchNetwork,
    refresh,
    refreshKey,
    getProvider: () => providerRef.current,
    getSigner: () => signerRef.current,
  };

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}
